/**
 * Helpers puros de filtros da página /casos (E13.F5).
 *
 * Separados de `CasosToolbar.tsx` para reduzir o tamanho do componente
 * e facilitar testes unitários dos predicados sem montar o DOM.
 */
import { ptBR } from '@/i18n/pt-BR';
import type { CaseStatus as BackendCaseStatus } from '@/lib/cases';
import { leituraEmConferencia, type EstadoDaLeituraDoCaso } from '@/lib/conferencia_da_leitura';

export interface CasosFilterState {
  cpf: string;
  contrato: string;
  // E179.F1: número do processo ou pasta, filtrado no servidor.
  processo: string;
  statuses: readonly BackendCaseStatus[];
  especime: string;
  dateFrom: string;
  dateTo: string;
  // E23.F24: atalho do revisor — só casos aguardando MINHA homologação.
  awaitingMyReview: boolean;
  // E140.F3 (GUS-143): o recorte técnico — casos com a leitura em conferência.
  leituraEmConferencia: boolean;
}

export const EMPTY_FILTERS: CasosFilterState = {
  cpf: '',
  contrato: '',
  processo: '',
  statuses: [],
  especime: '',
  dateFrom: '',
  dateTo: '',
  awaitingMyReview: false,
  leituraEmConferencia: false,
};

export const STATUS_VALUES: readonly BackendCaseStatus[] = [
  'draft',
  'analysing',
  'done',
  'conflict',
];

export interface ActiveChip {
  key: string;
  label: string;
  removeAria: string;
  onRemove: () => void;
}

function statusLabel(status: BackendCaseStatus): string {
  if (status === 'draft') return ptBR.casos.statusDraft;
  if (status === 'analysing') return ptBR.casos.statusAnalysing;
  if (status === 'done') return ptBR.casos.statusDone;
  return ptBR.casos.statusConflict;
}

function chip(key: string, label: string, ariaLabel: string, onRemove: () => void): ActiveChip {
  return {
    key,
    label,
    removeAria: ptBR.casos.filters.removeChipAria.replace('{{label}}', ariaLabel),
    onRemove,
  };
}

export function computeActiveChips(
  filters: CasosFilterState,
  patch: (next: Partial<CasosFilterState>) => void,
): ActiveChip[] {
  const chips: ActiveChip[] = [];
  if (filters.cpf.trim() !== '') {
    chips.push(chip('cpf', `CPF: ${filters.cpf}`, 'CPF', () => patch({ cpf: '' })));
  }
  if (filters.contrato.trim() !== '') {
    chips.push(
      chip('contrato', `Contrato: ${filters.contrato}`, 'Contrato', () => patch({ contrato: '' })),
    );
  }
  if (filters.processo.trim() !== '') {
    const rotulo = ptBR.casos.search.processoChip;
    chips.push(
      chip('processo', `${rotulo}: ${filters.processo}`, rotulo, () => patch({ processo: '' })),
    );
  }
  for (const s of filters.statuses) {
    chips.push(
      chip(`status:${s}`, `Status: ${statusLabel(s)}`, `Status ${statusLabel(s)}`, () =>
        patch({ statuses: filters.statuses.filter((x) => x !== s) }),
      ),
    );
  }
  if (filters.especime !== '') {
    chips.push(
      chip('especime', `Espécime: ${filters.especime}`, `Espécime ${filters.especime}`, () =>
        patch({ especime: '' }),
      ),
    );
  }
  if (filters.dateFrom !== '') {
    chips.push(
      chip('dateFrom', `De: ${filters.dateFrom}`, 'Data inicial', () => patch({ dateFrom: '' })),
    );
  }
  if (filters.dateTo !== '') {
    chips.push(chip('dateTo', `Até: ${filters.dateTo}`, 'Data final', () => patch({ dateTo: '' })));
  }
  if (filters.leituraEmConferencia) {
    const rotulo = ptBR.casos.leituraEmConferenciaFiltro;
    chips.push(chip('leitura', rotulo, rotulo, () => patch({ leituraEmConferencia: false })));
  }
  return chips;
}

export function hasAnyFilter(filters: CasosFilterState): boolean {
  return (
    filters.cpf.trim() !== '' ||
    filters.contrato.trim() !== '' ||
    filters.processo.trim() !== '' ||
    filters.statuses.length > 0 ||
    filters.especime !== '' ||
    filters.dateFrom !== '' ||
    filters.dateTo !== '' ||
    filters.awaitingMyReview ||
    filters.leituraEmConferencia
  );
}

interface FilterableCase {
  status: BackendCaseStatus;
  especime: string | null;
  received_at: string;
  // E23.F24 — usados pelo filtro do revisor (opcionais p/ não quebrar callers).
  usuario_id?: string;
  workflow_status?: string | null;
  leitura_da_conferencia?: EstadoDaLeituraDoCaso | null;
}

function passesStatus(c: FilterableCase, filters: CasosFilterState): boolean {
  if (filters.statuses.length === 0) return true;
  return filters.statuses.includes(c.status);
}

function passesEspecime(c: FilterableCase, filters: CasosFilterState): boolean {
  if (filters.especime === '') return true;
  return (c.especime ?? '') === filters.especime;
}

function passesDateRange(c: FilterableCase, filters: CasosFilterState): boolean {
  if (filters.dateFrom !== '' && c.received_at < filters.dateFrom) return false;
  if (filters.dateTo !== '' && c.received_at > filters.dateTo) return false;
  return true;
}

function passesAwaitingMyReview(c: FilterableCase, filters: CasosFilterState): boolean {
  if (!filters.awaitingMyReview) return true;
  // Aguardando MINHA homologação = pronto para o revisor, ponto. A exclusão do
  // criador saiu na E63.F1.C12: a ADR-0068 revogou o bloqueio de
  // auto-homologação, e manter o recorte antigo escondia justamente o caso que
  // o perito autônomo precisa achar aqui (F-E63.F1-06). Quem PODE homologar é
  // decidido pelo papel, fora deste filtro client-side — o checkbox só existe
  // para owner|revisor.
  return c.workflow_status === 'aguardando_revisor';
}

function passesLeitura(c: FilterableCase, filters: CasosFilterState): boolean {
  return !filters.leituraEmConferencia || leituraEmConferencia(c.leitura_da_conferencia);
}

export function applyClientFilters<T extends FilterableCase>(
  items: readonly T[],
  filters: CasosFilterState,
): T[] {
  return items.filter(
    (c) =>
      passesStatus(c, filters) &&
      passesEspecime(c, filters) &&
      passesDateRange(c, filters) &&
      passesAwaitingMyReview(c, filters) &&
      passesLeitura(c, filters),
  );
}
